/**
 * Wave 1 SEO cluster (part B): hoja de vida ATS, internships, 45+/50+,
 * online courses, icons/skill bars. Spanish and English. Merged from posts.js.
 */

export default [
  {
    slug: 'hoja-de-vida-ats',
    date: '2026-08-22',
    category: 'ats',
    related: ['hoja-de-vida-o-curriculum', 'plantillas-cv-compatibles-ats', 'cv-canva-y-ats'],
    es: {
      title: 'Hoja de vida compatible con ATS: formato para Colombia y LatAm',
      description:
        'La hoja de vida con columnas, cédula y barras también falla el filtro. Cómo pasar a un PDF parseable sin tirar tu archivo. ClarCV.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Si buscas cómo hacer una hoja de vida que pase el ATS, el problema casi nunca es “no tener experiencia”. Es el archivo. En Colombia, México y buena parte de LatAm se dice hoja de vida; en España, currículum. El software de selección no distingue el nombre: intenta leer de arriba abajo. Una plantilla de dos columnas, la cédula en la cabecera y barras de “Office 90%” se rompen igual en Bogotá que en Madrid. Nombres y campos por país: [hoja de vida vs currículum](/blog/hoja-de-vida-o-curriculum). La guía general de [CV para filtros ATS](/blog/cv-para-filtros-ats) aplica; aquí el parseo.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre qué cambia (cédula, foto, formato único de empleo público), qué no (el parser sigue odiando text boxes), y cómo regenerar el PDF que ya tienes. No pelees el término genérico “ejemplos de hoja de vida” contra portales de plantillas: pelea “hoja de vida ATS” con un archivo que se puede copiar. ClarCV (clarcv.com) encaja: subes el Word o PDF, ves fricción de estructura y bajas una plantilla de una columna.',
        },
        {
          type: 'p',
          text: 'No vas a “hackear” Computrabajo ni el ATS de una multilatina. Vas a dejar de enviar un diseño de Canva al mismo sitio donde pegan un parser. Si el proceso es empleo público colombiano (SIGEP / formato único), ese pliego manda: no sustituyas el formato oficial por uno “moderno”. Para empresa privada, el formato oficial suele ser peor que un PDF limpio.',
        },
        { type: 'h2', text: 'Hoja de vida, currículum y el mismo filtro' },
        {
          type: 'p',
          text: 'El reclutador en Medellín busca “hoja de vida”; el de Madrid, “CV”. Workday, SuccessFactors, Greenhouse y los portales grandes hacen el mismo trabajo: extraer nombre, fechas, títulos. Si tu archivo se llama HV_Juan.pdf y el de Ana se llama CV_Ana.pdf, el software no premia el nombre. Premia texto seleccionable, encabezados que reconoce (Experiencia, Formación, Habilidades) y keywords de la vacante. Más plantillas: [plantillas compatibles con ATS](/blog/plantillas-cv-compatibles-ats).',
        },
        {
          type: 'p',
          text: 'Costumbres que chocan con el parser: cédula o RFC en la primera línea, foto grande, dirección completa, estado civil, “pretensión salarial” fija, tablas para simular columnas. Parte de eso es hábito de formato único o de Word del colegio. En una multilatina o en un proceso remoto ES/US, quita cédula y estado civil del PDF que circula. Detalle de datos sensibles: [qué no poner en un CV](/blog/que-no-poner-en-el-cv).',
        },
        {
          type: 'ul',
          items: [
            'Privado / multilatina: ciudad, teléfono, email, LinkedIn. Sin cédula en el PDF.',
            'Empleo público que exige formato único: usa ese formato; no improvises un Canva.',
            'Foto: en varios países de LatAm se espera; en procesos US/UK, no. Si la dejas, que no empuje el email fuera del texto.',
            'Una página o dos según trayectoria, igual que un CV europeo. Tres páginas de narrativa no ayudan al ATS.',
          ],
        },
        { type: 'h2', text: 'La prueba de dos minutos (hazla con tu archivo actual)' },
        {
          type: 'p',
          text: 'Abre el PDF. Selecciona nombre, email y tres viñetas. Pégalos en el bloc de notas. Si el orden sale caótico, si falta el correo o si aparecen símbolos raros, esa hoja de vida no está lista para el filtro. Misma prueba que en [CV de Canva y ATS](/blog/cv-canva-y-ats). Canva y las plantillas “creativas” de Word son el origen más frecuente.',
        },
        {
          type: 'p',
          text: 'Iconos de WhatsApp en lugar de la palabra teléfono, barras de skill, y “perfil profesional” metido en un recuadro: el humano lo entiende; el parser mezcla. Traduce iconos a texto. Pon fechas junto al puesto, no en una columna huérfana a la izquierda.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Tu hoja de vida de Canva falla el mismo filtro en Bogotá y en Madrid',
          lead: 'Sube el archivo que ya tienes. Si el texto no se pega en orden, regenera una versión de una columna que el ATS pueda leer.',
          from: 'Columnas y cédula',
          to: 'Hoja de vida parseable',
          button: 'Subir mi hoja de vida',
        },
        { type: 'h2', text: 'Keywords de la vacante, no de la plantilla' },
        {
          type: 'p',
          text: 'El ATS colombiano o mexicano no busca “proactivo”. Busca el cargo y las herramientas del anuncio: SAP, Excel, cartera, turnos, Python. Extrae 10–15 términos de la vacante e intégralos en logros si son verdad. Guía: [palabras clave ATS](/blog/palabras-clave-cv-ats). No copies el anuncio entero. No disfraces un curso de dos horas como “experto SAP”.',
        },
        {
          type: 'p',
          text: 'Si aplicas a España desde LatAm, alinea vocabulario (currículum, puesto, formación) y quita campos que en Europa sobran. El archivo puede ser el mismo contenido en otra plantilla, no otra biografía. Idioma del anuncio = idioma del PDF.',
        },
        { type: 'h2', text: 'Cómo regenerar sin reescribir tu trayectoria' },
        {
          type: 'p',
          text: 'Conserva empresas, fechas y cifras que puedas defender. Cambia el continente: una columna, encabezados estándar, contacto en texto al inicio. En ClarCV subes PDF o Word, el análisis señala claridad y fricción (no un score mágico contra un ATS concreto) y aplicas plantilla A4. Empieza gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Interfaz en español; el documento de salida sigue el idioma que tú elijas.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Hoja de vida y currículum son documentos distintos?',
              a: 'En la práctica, no para el ATS. Es el mismo tipo de archivo con otro nombre. El formato único de empleo público sí es otro documento: síguelo cuando lo exijan.',
            },
            {
              q: '¿Debo poner la cédula en Colombia?',
              a: 'En muchos PDFs locales sí se ve. Para empresa privada y portales grandes, mejor no en el archivo que se reenvía. El formulario del portal puede pedirla aparte.',
            },
            {
              q: '¿Computrabajo lee PDFs de Canva?',
              a: 'A veces extrae mal. La prueba del bloc de notas manda más que el logo del portal.',
            },
            {
              q: '¿Una hoja de vida de dos columnas “se ve más profesional”?',
              a: 'Para un humano en una entrevista de diseño, a veces. Para el filtro, suele ser peor. Dos archivos: uno parseable, uno visual si el canal es humano.',
            },
            {
              q: '¿ClarCV entiende “hoja de vida”?',
              a: 'Lee el archivo (PDF/Word), no el nombre cultural. El contenido es un CV. Las plantillas son A4 con texto real.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'ATS-friendly hoja de vida: format for Colombia and LatAm',
      description:
        'A hoja de vida with columns, ID number, and skill bars fails the filter too. How to get a parseable PDF without throwing the file away. ClarCV.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'If you are searching how to make a hoja de vida that passes ATS, the issue is rarely “no experience”. It is the file. In Colombia, Mexico, and much of LatAm people say hoja de vida; in Spain, currículum. Selection software does not care about the name: it tries to read top to bottom. A two-column template, a national ID in the header, and “Office 90%” bars break the same way in Bogotá as in Madrid. Names and fields by country: [hoja de vida vs CV](/blog/hoja-de-vida-o-curriculum). The general [CV for ATS filters](/blog/cv-para-filtros-ats) still applies; this page is parsing.',
        },
        {
          type: 'p',
          text: 'This guide covers what changes (ID number, photo, official public-employment forms), what does not (parsers still hate text boxes), and how to regenerate the PDF you already have. Do not fight the generic “hoja de vida examples” SERP against template mills: fight “ATS hoja de vida” with a file you can copy. ClarCV (clarcv.com) fits: upload Word or PDF, see structure friction, download a one-column template.',
        },
        {
          type: 'p',
          text: 'You will not “hack” Computrabajo or a multilatina’s ATS. You will stop sending a Canva design to the same place they paste a parser. If the process is Colombian public employment (SIGEP / official unique form), that spec wins: do not replace the official form with a “modern” one. For private companies, the official form is usually worse than a clean PDF.',
        },
        { type: 'h2', text: 'Hoja de vida, CV, and the same filter' },
        {
          type: 'p',
          text: 'A recruiter in Medellín searches “hoja de vida”; one in Madrid searches “CV”. Workday, SuccessFactors, Greenhouse, and large boards do the same job: extract name, dates, titles. If your file is HV_Juan.pdf and Ana’s is CV_Ana.pdf, the software does not reward the filename. It rewards selectable text, headings it recognises (Experience, Education, Skills), and keywords from the vacancy. More on templates: [ATS-friendly CV templates](/blog/plantillas-cv-compatibles-ats).',
        },
        {
          type: 'p',
          text: 'Habits that hit the parser: ID or tax number on line one, a large photo, full street address, marital status, a fixed salary expectation, tables pretending to be columns. Some of that comes from official forms or school Word files. At a multilatina or in a remote ES/US process, drop ID and marital status from the PDF that circulates. Sensitive-data detail: [what not to put on a CV](/blog/que-no-poner-en-el-cv).',
        },
        {
          type: 'ul',
          items: [
            'Private / multilatina: city, phone, email, LinkedIn. No ID number on the PDF.',
            'Public employment that requires an official form: use that form; do not improvise Canva.',
            'Photo: often expected in parts of LatAm; not in US/UK processes. If you keep it, it must not push email out of the text layer.',
            'One page or two by career length, same as a European CV. Three pages of narrative do not help ATS.',
          ],
        },
        { type: 'h2', text: 'The two-minute test (use your current file)' },
        {
          type: 'p',
          text: 'Open the PDF. Select name, email, and three bullets. Paste into Notepad. If the order is chaos, the email is missing, or odd symbols appear, that hoja de vida is not filter-ready. Same test as [Canva CV and ATS](/blog/cv-canva-y-ats). Canva and “creative” Word templates are the usual source.',
        },
        {
          type: 'p',
          text: 'WhatsApp icons instead of the word phone, skill bars, and a “professional profile” stuffed in a box: a human gets it; the parser mixes it. Translate icons to text. Put dates next to the role, not in an orphan column on the left.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Your Canva hoja de vida fails the same filter in Bogotá and Madrid',
          lead: 'Upload the file you already have. If text does not paste in order, regenerate a one-column version ATS can read.',
          from: 'Columns and ID number',
          to: 'A parseable hoja de vida',
          button: 'Upload my hoja de vida',
        },
        { type: 'h2', text: 'Vacancy keywords, not template keywords' },
        {
          type: 'p',
          text: 'A Colombian or Mexican ATS is not searching for “proactive”. It is searching for the title and tools in the ad: SAP, Excel, book of business, shifts, Python. Pull 10–15 terms from the vacancy and weave them into achievements if they are true. Guide: [ATS keywords](/blog/palabras-clave-cv-ats). Do not paste the whole ad. Do not dress a two-hour course as “SAP expert”.',
        },
        {
          type: 'p',
          text: 'If you are applying to Spain from LatAm, align vocabulary (currículum, role titles, education) and drop fields Europe does not want. The file can be the same content in another template, not another biography. Ad language = PDF language.',
        },
        { type: 'h2', text: 'How to regenerate without rewriting your career' },
        {
          type: 'p',
          text: 'Keep employers, dates, and figures you can defend. Change the container: one column, standard headings, contact as text at the top. In ClarCV you upload PDF or Word, analysis flags clarity and friction (not a magic score against a named ATS), and you apply an A4 template. Start free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Spanish UI; output language is yours.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Are hoja de vida and CV different documents?',
              a: 'In practice, not for ATS. Same kind of file, different name. The official public-employment form is a different document: follow it when required.',
            },
            {
              q: 'Must I put my cédula in Colombia?',
              a: 'Many local PDFs still do. For private companies and large boards, better not on the file that gets forwarded. The portal form can ask separately.',
            },
            {
              q: 'Does Computrabajo read Canva PDFs?',
              a: 'Sometimes it extracts poorly. The Notepad test matters more than the board’s logo.',
            },
            {
              q: 'Does a two-column hoja de vida “look more professional”?',
              a: 'For a human in a design interview, sometimes. For the filter, usually worse. Two files: one parseable, one visual if the channel is human.',
            },
            {
              q: 'Does ClarCV understand “hoja de vida”?',
              a: 'It reads the file (PDF/Word), not the cultural name. The content is a CV. Templates are A4 with real text.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-practicas',
    date: '2026-08-22',
    category: 'career',
    related: ['primer-empleo-cv', 'tfg-tfm-en-el-cv', 'idiomas-en-el-cv'],
    es: {
      title: 'CV para prácticas y becas: qué poner si aún no tienes contrato',
      description:
        'TFG, prácticas, voluntariado y cursos en viñetas que se pueden defender. Cómo reescribir el archivo que ya tienes, no una plantilla vacía. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Un CV para prácticas no se gana inventando un empleo. Se gana convirtiendo lo que ya hiciste —TFG, prácticas curriculares, extraescolares, un verano en hostelería— en viñetas con verbo, herramienta y alcance. Quien busca “CV para beca” suele tener un Europass de cuatro páginas o un Canva con la foto grande y el grado en letra pequeña. El filtro de una empresa que publica becas en InfoJobs o en su web usa el mismo ATS que el resto. El archivo tiene que parsear.',
        },
        {
          type: 'p',
          text: 'Esta guía no es “cómo hacer un CV desde cero”. Es qué subir, qué bajar y qué cortar en el documento que ya tienes. Hermanas: [primer empleo](/blog/primer-empleo-cv) y [CV sin experiencia](/blog/cv-sin-experiencia). Aquí el ángulo es prácticas y becas (incluida universidad y programas de empresa). ClarCV: subes el PDF, el análisis señala densidad y estructura, regeneras una página A4 con texto real.',
        },
        {
          type: 'p',
          text: 'No vas a disfrazar un trabajo de clase como “analista en Deloitte”. Vas a titular con honestidad: “Prácticas de marketing, Agencia X (feb–may 2026)”. El mentir se cae en la primera llamada al tutor.',
        },
        { type: 'h2', text: 'Orden de secciones cuando no hay contrato indefinido' },
        {
          type: 'p',
          text: 'Resumen de 3 líneas (grado, foco, un hecho). Luego la experiencia más cercana al puesto: prácticas, TFG aplicado, proyecto, voluntariado de función clara. Formación con nota solo si es un imán (premio, ranking). Idiomas con MCER: en becas internacionales el [nivel A1–C2](/blog/idiomas-en-el-cv) pesa más que un hobby. Habilidades: herramientas que usaste, no “Office y trabajo en equipo” sin prueba.',
        },
        {
          type: 'ul',
          items: [
            'Prácticas: empresa, fechas mes/año, 3–4 viñetas (qué tocaste: CRM, Excel, almacén, aula).',
            'TFG/TFM: título corto, tu rol, método o stack, resultado (nota, prototipo, dataset).',
            'Proyectos de asignatura o hackathon: problema, tu parte, herramienta, entrega.',
            'Trabajos de verano / extra: sí, si demuestran turnos, caja, idiomas, responsabilidad.',
            'Cursos: solo los que puedes defender en 30 segundos; el resto a [cursos online en el CV](/blog/cursos-online-en-el-cv).',
          ],
        },
        {
          type: 'p',
          text: 'Antes: “Colaboré en diversas tareas del departamento y adquirí habilidades transversales.” Después: “Actualicé 80 fichas de producto en Prestashop y preparé el informe semanal de stock en Excel para el responsable de tienda.” Misma práctica de tres meses. Solo una versión pasa un screening de becas de e-commerce.',
        },
        { type: 'h2', text: 'Becas, Erasmus y el Europass largo' },
        {
          type: 'p',
          text: 'Algunas convocatorias universitarias aún piden Europass. Si el pliego lo exige, úsalo. Si la beca es en una empresa privada, el Europass de tres páginas suele perder. Guía: [Europass o CV moderno](/blog/europass-o-cv-moderno). Extrae logros y mételos en una página parseable. Erasmus: el semestre es experiencia de idioma y autonomía; una viñeta basta, no un diario de viaje.',
        },
        {
          type: 'p',
          text: 'Carta de motivación: si la piden, es otro archivo. El CV no es la carta. El email de envío puede ser tres párrafos: [enviar CV por email](/blog/enviar-cv-por-email).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El TFG no es un párrafo de 15 líneas',
          lead: 'Sube el CV o Europass que ya tienes. Convierte prácticas y proyecto en viñetas cortas y un PDF de una página que el filtro de becas pueda leer.',
          from: 'Europass de 4 páginas',
          to: 'Una página con prueba',
          button: 'Rehacer mi CV junior',
        },
        { type: 'h2', text: 'Lo que no debes inflar' },
        {
          type: 'p',
          text: 'No conviertas “asistí a reuniones” en “lideré la estrategia comercial”. No pongas Python si solo corriste un notebook del profesor. No listes 15 cursos Udemy. El seleccionador de becas ve cientos de PDFs iguales; gana quien tiene dos hechos y un archivo que se abre en el móvil. Foto: según país; no dejes que la foto coma el nombre. DNI: fuera del PDF.',
        },
        { type: 'h2', text: 'Regenerar el archivo, no el CV en blanco' },
        {
          type: 'p',
          text: 'Si ya tienes un Word de la facultad, úsalo como materia prima. En ClarCV lo subes, ves claridad y estructura, aplicas plantilla ATS. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Conserva fechas reales. El modelo no debe inventar una práctica en Inditex.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Una página o dos para prácticas?',
              a: 'Una. Si no llenas, no rellenes con hobbies. Añade un proyecto concreto o recorta márgenes gigantes.',
            },
            {
              q: '¿Pongo la nota media?',
              a: 'Si es un imán (premio, 8,5+ en un grado exigente) o si la convocatoria la pide. Si no, el TFG bien escrito vale más.',
            },
            {
              q: '¿Las prácticas no remuneradas cuentan como experiencia?',
              a: 'Sí, con el título “prácticas”. No las etiquetes como empleo indefinido.',
            },
            {
              q: '¿Puedo usar el mismo CV para diez becas?',
              a: 'La plantilla sí; las keywords y el resumen, no. Adapta 15 minutos al anuncio: [adaptar el CV a una oferta](/blog/adaptar-cv-a-una-oferta).',
            },
            {
              q: '¿ClarCV rellena prácticas inventadas?',
              a: 'No debe. Conserva lo que hay en el archivo. Tú aportas los hechos.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Internship and grant CVs: what to put when you have no contract yet',
      description:
        'Dissertation, internships, volunteering, and courses as bullets you can defend. How to rewrite the file you already have, not a blank template. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'An internship CV is not won by inventing a job. It is won by turning what you already did — dissertation, credit-bearing internship, clubs, a summer in hospitality — into bullets with a verb, a tool, and scope. People searching “CV for a grant” often have a four-page Europass or a Canva file with a large photo and the degree in small type. A company posting internships on a job board uses the same ATS as everyone else. The file has to parse.',
        },
        {
          type: 'p',
          text: 'This guide is not “how to make a CV from scratch”. It is what to raise, what to lower, and what to cut in the document you already have. Siblings: [first job](/blog/primer-empleo-cv) and [CV with no experience](/blog/cv-sin-experiencia). The angle here is internships and grants (university and company programmes). ClarCV: upload the PDF, analysis flags density and structure, regenerate one A4 page with real text.',
        },
        {
          type: 'p',
          text: 'You will not dress a class assignment as “analyst at Deloitte”. You will title it honestly: “Marketing internship, Agency X (Feb–May 2026)”. Lying dies on the first call to your tutor.',
        },
        { type: 'h2', text: 'Section order when there is no permanent contract' },
        {
          type: 'p',
          text: 'A 3-line summary (degree, focus, one fact). Then the experience closest to the role: internship, applied dissertation, project, volunteering with a clear function. Education with a grade only if it is a magnet (prize, ranking). Languages with CEFR: on international grants [A1–C2](/blog/idiomas-en-el-cv) weighs more than a hobby. Skills: tools you used, not “Office and teamwork” with no proof.',
        },
        {
          type: 'ul',
          items: [
            'Internship: employer, month/year dates, 3–4 bullets (what you touched: CRM, Excel, warehouse, classroom).',
            'Dissertation: short title, your role, method or stack, outcome (grade, prototype, dataset).',
            'Course projects or hackathons: problem, your part, tool, delivery.',
            'Summer / extra jobs: yes, if they show shifts, till, languages, responsibility.',
            'Courses: only those you can defend in 30 seconds; the rest in [online courses on a CV](/blog/cursos-online-en-el-cv).',
          ],
        },
        {
          type: 'p',
          text: 'Before: “I collaborated on various department tasks and acquired transferable skills.” After: “Updated 80 product records in Prestashop and prepared the weekly stock report in Excel for the store manager.” Same three-month internship. Only one version survives an e-commerce grant screen.',
        },
        { type: 'h2', text: 'Grants, Erasmus, and the long Europass' },
        {
          type: 'p',
          text: 'Some university calls still want Europass. If the spec requires it, use it. If the grant is at a private company, a three-page Europass usually loses. Guide: [Europass or a modern CV](/blog/europass-o-cv-moderno). Extract achievements into one parseable page. Erasmus: the semester is language and autonomy; one bullet is enough, not a travel diary.',
        },
        {
          type: 'p',
          text: 'Motivation letter: if they ask, it is another file. The CV is not the letter. The sending email can be three paragraphs: [emailing your CV](/blog/enviar-cv-por-email).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Your dissertation is not a 15-line paragraph',
          lead: 'Upload the CV or Europass you already have. Turn internships and the project into short bullets and a one-page PDF a grant filter can read.',
          from: '4-page Europass',
          to: 'One page with proof',
          button: 'Rebuild my junior CV',
        },
        { type: 'h2', text: 'What you must not inflate' },
        {
          type: 'p',
          text: 'Do not turn “I attended meetings” into “I led commercial strategy”. Do not list Python if you only ran the lecturer’s notebook. Do not list 15 Udemy courses. Grant screeners see hundreds of identical PDFs; the winner has two facts and a file that opens on a phone. Photo: depends on country; do not let the photo eat your name. National ID: off the PDF.',
        },
        { type: 'h2', text: 'Regenerate the file, not a blank CV' },
        {
          type: 'p',
          text: 'If you already have a faculty Word file, use it as raw material. In ClarCV you upload it, see clarity and structure, apply an ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Keep real dates. The model must not invent an internship at Inditex.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'One page or two for internships?',
              a: 'One. If it looks empty, do not fill with hobbies. Add a concrete project or cut giant margins.',
            },
            {
              q: 'Should I put my GPA?',
              a: 'If it is a magnet (prize, 8.5+ on a tough degree) or the call asks. If not, a well-written dissertation is worth more.',
            },
            {
              q: 'Do unpaid internships count as experience?',
              a: 'Yes, titled “internship”. Do not label them as a permanent job.',
            },
            {
              q: 'Can I use the same CV for ten grants?',
              a: 'The template yes; keywords and summary no. Spend 15 minutes tailoring: [tailor your CV to a job](/blog/adaptar-cv-a-una-oferta).',
            },
            {
              q: 'Does ClarCV fill in invented internships?',
              a: 'It must not. It keeps what is in the file. You supply the facts.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-mayores-de-50',
    date: '2026-08-22',
    category: 'career',
    related: ['hueco-laboral-en-el-cv', 'cambiar-de-sector-cv', 'cv-demasiado-largo'],
    es: {
      title: 'CV si tienes más de 45 o 50: cómo no quedar fuera del filtro',
      description:
        'El ATS no “ve” tu edad; ve tres páginas y 1989 en la cabecera. Cómo recortar trayectoria reciente sin borrarte. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'El filtro no tiene un campo “descartar mayores de 50”. Tiene un PDF de tres páginas, la fecha de nacimiento en la primera línea y el primer puesto de 1992 ocupando medio folio. Quien busca “CV mayores de 50” o “CV senior 45 años” suele tener un archivo honesto y largo. El problema es señal: lo reciente se hunde bajo lo antiguo. Recortar no es mentir. Es dejar que los últimos 10–15 años se puedan leer en diez segundos.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre qué fechas mostrar, cómo compactar roles antiguos, si quitar la edad, y cómo no parecer “desconectado” (herramientas, keywords). Hermanas: [hueco laboral](/blog/hueco-laboral-en-el-cv), [cambiar de sector](/blog/cambiar-de-sector-cv), [CV demasiado largo](/blog/cv-demasiado-largo). ClarCV sirve para regenerar el archivo que ya tienes en una plantilla ATS de dos páginas máximo, sin Canva de tres columnas.',
        },
        {
          type: 'p',
          text: 'No vas a fingir 35 años. Vas a quitar la fecha de nacimiento, a agrupar 1990–2005 en cuatro líneas, y a poner herramientas que sí usas. Un screening por videollamada llega si el PDF no se autoelimina antes.',
        },
        { type: 'h2', text: 'Quita la edad de la cabecera' },
        {
          type: 'p',
          text: 'Fecha de nacimiento, “55 años”, foto de carnet de 2008: fuera. En España aún se ve; no ayuda. El sesgo entra antes que tus logros. Ciudad, teléfono, email, LinkedIn. Detalle: [qué no poner](/blog/que-no-poner-en-el-cv). Si un proceso pide edad en un formulario, el formulario; no el PDF que se reenvía.',
        },
        { type: 'h2', text: 'Los últimos 10–15 años mandan' },
        {
          type: 'p',
          text: 'Detalle (viñetas con cifra) solo en roles recientes alineados al objetivo. Antes: un bloque “Experiencia anterior (1992–2008): administrativo, comercial, supervisor de tienda” en tres líneas, o los títulos sin viñetas. No borres 30 años: comprímelos. Si cambias de sector, traduce títulos internos a nombres de mercado en lo reciente; lo antiguo puede quedar con el nombre real y menos detalle.',
        },
        {
          type: 'ul',
          items: [
            'Dos páginas como techo salvo academia o C-level muy denso.',
            'Tecnología: nombra lo que usas ahora (Excel, SAP, Salesforce, Teams), no “informática a nivel usuario” de 1998.',
            'Formación continua reciente (un certificado 2023) pesa más que un máster de 1995 sin uso.',
            'Huecos: explícalos con una línea, no con un agujero. Ver guía de hueco laboral.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: tres páginas, cada puesto con ocho viñetas de tareas. Después: página 1 = último rol + anterior relevante; página 2 = resto compacto + skills. El ATS encuentra fechas recientes juntas al título. El humano ve que sigues en activo.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El filtro no lee tu edad; lee un PDF de tres páginas',
          lead: 'Sube el CV largo que ya tienes. Recorta lo antiguo, quita la fecha de nacimiento y regenera dos páginas que se puedan parsear.',
          from: '3 páginas y 1989 en la cabecera',
          to: '2 páginas, hechos recientes',
          button: 'Acortar mi CV',
        },
        { type: 'h2', text: 'Keywords de 2026, no jerga de 2004' },
        {
          type: 'p',
          text: 'Si el anuncio dice “customer success” y tu tarjeta decía “atención a grandes cuentas”, pon ambos: título de mercado y, si hace falta, el interno entre paréntesis. Extrae herramientas del anuncio si las usaste. No copies “ágil, disruptivo, nativo digital”. Sí: un logro con CRM actual. Guía: [adaptar el CV a una oferta](/blog/adaptar-cv-a-una-oferta).',
        },
        { type: 'h2', text: 'Diseño: una columna, texto real' },
        {
          type: 'p',
          text: 'El CV “serio” de Word con tablas y el de Canva oscuro fallan igual. Una columna, encabezados estándar, PDF seleccionable. En ClarCV subes el archivo, análisis de claridad y estructura, plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Tú validas que no se haya perdido un cargo: el modelo no debe inventar un puesto de 2019.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Debo ocultar el primer empleo de 1988?',
              a: 'No lo borres del todo si da contexto de sector. Compáctalo. El vacío de 15 años extraña más que un bloque corto “anterior”.',
            },
            {
              q: '¿Foto sí o no después de los 50?',
              a: 'En España, foto profesional reciente o ninguna; no una de hace quince años. En UK/US, ninguna. Ver guía de foto.',
            },
            {
              q: '¿El ATS descarta por año de titulación?',
              a: 'Algunos humanos sí miran el año. El parser extrae fechas. Por eso la cabecera no debe gritar 1972. La titulación puede ir sin el año si el anuncio no lo pide y te perjudica; no mientas si te lo preguntan.',
            },
            {
              q: '¿Puedo tener dos CVs (uno “completo”)?',
              a: 'Sí: el largo para una red de confianza; el de dos páginas para portales. El de portales es el que debe parsear.',
            },
            {
              q: '¿ClarCV borra mi trayectoria antigua?',
              a: 'Tú decides el recorte en el editor. El análisis puede señalar longitud; no debe inventar una carrera más corta.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV if you are over 45 or 50: how not to lose the filter',
      description:
        'ATS does not “see” your age; it sees three pages and 1989 in the header. How to trim recent career without erasing yourself. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'The filter does not have a “reject over-50” field. It has a three-page PDF, date of birth on line one, and a 1992 first job eating half a page. People searching “CV over 50” or “senior CV 45” usually have an honest, long file. The problem is signal: the recent work drowns under the old. Cutting is not lying. It is letting the last 10–15 years be readable in ten seconds.',
        },
        {
          type: 'p',
          text: 'This guide covers which dates to show, how to compress old roles, whether to drop age, and how not to look “disconnected” (tools, keywords). Siblings: [employment gap](/blog/hueco-laboral-en-el-cv), [career change](/blog/cambiar-de-sector-cv), [CV too long](/blog/cv-demasiado-largo). ClarCV is how you regenerate the file you already have into an ATS template of two pages max, without three-column Canva.',
        },
        {
          type: 'p',
          text: 'You will not pretend to be 35. You will remove date of birth, group 1990–2005 into four lines, and name tools you actually use. A video screen happens if the PDF does not delete itself first.',
        },
        { type: 'h2', text: 'Take age out of the header' },
        {
          type: 'p',
          text: 'Date of birth, “55 years old”, a 2008 ID photo: out. You still see it in Spain; it does not help. Bias arrives before your achievements. City, phone, email, LinkedIn. Detail: [what not to put](/blog/que-no-poner-en-el-cv). If a process asks age on a form, use the form — not the PDF that gets forwarded.',
        },
        { type: 'h2', text: 'The last 10–15 years lead' },
        {
          type: 'p',
          text: 'Detail (bullets with numbers) only on recent roles aligned to the target. Earlier: one block “Previous experience (1992–2008): admin, sales, store supervisor” in three lines, or titles with no bullets. Do not delete 30 years: compress them. If you are changing sector, translate internal titles to market names on recent roles; older ones can keep the real name with less detail.',
        },
        {
          type: 'ul',
          items: [
            'Two pages as a ceiling except academia or very dense C-level.',
            'Technology: name what you use now (Excel, SAP, Salesforce, Teams), not “computer literacy” from 1998.',
            'Recent training (a 2023 certificate) weighs more than a 1995 master’s with no later use.',
            'Gaps: explain them in one line, not a hole. See the employment-gap guide.',
          ],
        },
        {
          type: 'p',
          text: 'Before: three pages, every job with eight task bullets. After: page 1 = latest role + previous relevant one; page 2 = the rest compressed + skills. ATS finds recent dates next to the title. A human sees you are still active.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The filter does not read your age; it reads a three-page PDF',
          lead: 'Upload the long CV you already have. Trim the old, drop date of birth, and regenerate two pages that can parse.',
          from: '3 pages and 1989 in the header',
          to: '2 pages, recent proof',
          button: 'Shorten my CV',
        },
        { type: 'h2', text: '2026 keywords, not 2004 jargon' },
        {
          type: 'p',
          text: 'If the ad says “customer success” and your card said “key-account service”, put both: market title and, if needed, the internal one in brackets. Pull tools from the ad if you used them. Do not paste “agile, disruptive, digital native”. Do: one achievement with a current CRM. Guide: [tailor your CV to a job](/blog/adaptar-cv-a-una-oferta).',
        },
        { type: 'h2', text: 'Layout: one column, real text' },
        {
          type: 'p',
          text: 'The “serious” Word CV with tables and the dark Canva one fail the same way. One column, standard headings, selectable PDF. In ClarCV you upload the file, get clarity and structure analysis, apply an A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. You check that a role was not dropped: the model must not invent a 2019 job.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I hide a 1988 first job?',
              a: 'Do not delete it entirely if it gives sector context. Compress it. A 15-year hole looks weirder than a short “earlier career” block.',
            },
            {
              q: 'Photo after 50?',
              a: 'In Spain, a recent professional photo or none — not one from fifteen years ago. In the UK/US, none. See the photo guide.',
            },
            {
              q: 'Does ATS reject based on graduation year?',
              a: 'Some humans do look at the year. The parser extracts dates. That is why the header must not shout 1972. The degree can omit the year if the ad does not ask and it hurts you; do not lie if they ask.',
            },
            {
              q: 'Can I keep two CVs (one “full”)?',
              a: 'Yes: the long one for a trusted network; the two-page one for boards. The board one is the one that must parse.',
            },
            {
              q: 'Does ClarCV delete my early career?',
              a: 'You decide the cut in the editor. Analysis may flag length; it must not invent a shorter career.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cursos-online-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['palabras-clave-cv-ats', 'cv-sin-experiencia', 'primer-empleo-cv'],
    es: {
      title: 'Cursos de Udemy, Coursera y LinkedIn en el CV: cuáles cuentan',
      description:
        'Udemy no es un máster. El ATS busca la skill, no el marketplace. Cómo elegir 4 cursos y dónde ponerlos. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Los cursos online en el CV cuentan cuando son una skill que puedes demostrar, no cuando son una lista de 40 diplomas Udemy. Quien busca “Udemy en el CV” suele tener un PDF junior hinchado o un senior que quiere parecer actualizado. El ATS no puntúa “Udemy”. Puntúa Python, Excel, HubSpot, si esas palabras están en el anuncio y en tu experiencia. El marketplace es ruido; la herramienta es la keyword.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre cuáles dejar, cuáles mandar al cajón, dónde colocarlos (formación vs skills vs viñeta de un puesto) y cómo no mezclar un MOOC de 8 horas con un máster. Relación: [palabras clave ATS](/blog/palabras-clave-cv-ats), [CV sin experiencia](/blog/cv-sin-experiencia). ClarCV ayuda cuando la sección “formación complementaria” ocupa más que la experiencia: subes el archivo y recortas.',
        },
        {
          type: 'p',
          text: 'No vas a borrar todo lo que aprendiste en pijama. Vas a dejar cuatro líneas que sobrevivan a “¿qué hiciste en ese curso?”. Si la respuesta es “vi los vídeos”, fuera del PDF.',
        },
        { type: 'h2', text: 'La regla de los 30 segundos' },
        {
          type: 'p',
          text: 'Déjalo si puedes contar un entregable: un dashboard, un anuncio real, un repo, un certificado proctorado (Google, AWS, Meta, un Coursera con examen). Quítalo si es un pack de 20 cursos a 12 €, un LinkedIn Learning de una hora, o “introducción a” algo que no usaste después. Un certificado Google Data Analytics con proyecto pesa. “Complete Python Bootcamp” sin un solo script en GitHub no.',
        },
        {
          type: 'ul',
          items: [
            'Sí: certificación con ID o examen (AWS, Google, Microsoft, HubSpot Academy, DELE).',
            'Sí: curso largo aplicado a un proyecto que ya está en Experiencia o Proyectos.',
            'No: 15 intros de Udemy del mismo año.',
            'No: duplicar en Skills y en Cursos la misma palabra siete veces (relleno).',
            'No: poner la plataforma como si fuera la universidad (“Máster Udemy”).',
          ],
        },
        {
          type: 'p',
          text: 'Antes: Formación complementaria — 18 cursos (Udemy, Domestika, YouTube). Después: Google UX Design (Coursera, 2024) — prototipo en Figma de app de citas médicas. Excel (Power Query) aplicado al reporting mensual en prácticas. Dos líneas. El ATS encuentra Figma y Excel. El humano tiene una pregunta inteligente.',
        },
        { type: 'h2', text: 'Dónde ponerlo' },
        {
          type: 'p',
          text: 'Si el curso es el único signal de una herramienta que pide la oferta, que aparezca en Skills y en una viñeta (“modelé el embudo en GA4 tras el curso X”). Si es extra, un bloque corto “Certificaciones” al final, con año. No lo pongas encima de las prácticas reales. Un junior con el grado a medias no debe abrir con Udemy. Ver [CV para prácticas](/blog/cv-para-practicas).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El ATS busca Excel, no el logo de Udemy',
          lead: 'Si tu PDF lista 40 cursos, el filtro no se impresiona. Quédate con cuatro que puedas defender y regenera el archivo.',
          from: '40 diplomas',
          to: '4 skills defendibles',
          button: 'Ordenar mi formación',
        },
        { type: 'h2', text: 'Coursera, LinkedIn Learning, Platzi, Domestika' },
        {
          type: 'p',
          text: 'La plataforma no da prestigio por sí sola. Coursera con universidad asociada y proyecto > LinkedIn Learning de un vídeo. Platzi o una escuela reconocida en tu mercado puede ser señal local. Domestika: útil en diseño si hay portfolio; inútil como lista. No mientas la duración ni “certificado profesional” si fue un curso libre.',
        },
        { type: 'h2', text: 'Limpiar el PDF que ya tienes' },
        {
          type: 'p',
          text: 'Borra el listado, deja cuatro, pasa las herramientas a Skills en texto (no barras). En ClarCV subes el Word o PDF, ves densidad y estructura, plantilla ATS. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. El modelo no debe inventar un AWS Certified que no está en el archivo.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo el enlace del certificado?',
              a: 'Si es corto y público, sí, en texto. Un QR enorme, no. El ATS no sigue el QR.',
            },
            {
              q: '¿Un bootcamp de 6 meses cuenta como formación?',
              a: 'Sí, en Formación, con fechas y proyecto. No lo escondas entre micros de Udemy.',
            },
            {
              q: '¿Y los cursos de empresa (Salesforce Trailhead)?',
              a: 'Cuentan si el anuncio pide esa herramienta. Una línea con el nivel (Ranger, etc.) si es reconocible.',
            },
            {
              q: '¿Debo poner cursos abandonados al 40%?',
              a: 'No.',
            },
            {
              q: '¿ClarCV elige mis cuatro cursos?',
              a: 'Tú recortas. El análisis puede señalar que la sección está hinchada.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Udemy, Coursera, and LinkedIn courses on a CV: which ones count',
      description:
        'Udemy is not a master’s. ATS looks for the skill, not the marketplace. How to pick 4 courses and where to put them. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Online courses on a CV count when they are a skill you can prove, not when they are a list of 40 Udemy diplomas. People searching “Udemy on a CV” often have a bloated junior PDF or a senior who wants to look current. ATS does not score “Udemy”. It scores Python, Excel, HubSpot, if those words are in the ad and in your experience. The marketplace is noise; the tool is the keyword.',
        },
        {
          type: 'p',
          text: 'This guide covers what to keep, what to drawer, where to place them (education vs skills vs a job bullet), and how not to mix an 8-hour MOOC with a master’s. Related: [ATS keywords](/blog/palabras-clave-cv-ats), [CV with no experience](/blog/cv-sin-experiencia). ClarCV helps when “additional training” is longer than experience: upload the file and trim.',
        },
        {
          type: 'p',
          text: 'You will not delete everything you learned in pyjamas. You will leave four lines that survive “what did you do on that course?”. If the answer is “I watched the videos”, it is off the PDF.',
        },
        { type: 'h2', text: 'The 30-second rule' },
        {
          type: 'p',
          text: 'Keep it if you can name a deliverable: a dashboard, a real ad, a repo, a proctored certificate (Google, AWS, Meta, a Coursera with an exam). Cut it if it is a €12 pack of 20 courses, a one-hour LinkedIn Learning, or an “intro to” something you never used later. A Google Data Analytics certificate with a project weighs. “Complete Python Bootcamp” with no script on GitHub does not.',
        },
        {
          type: 'ul',
          items: [
            'Yes: a certificate with an ID or exam (AWS, Google, Microsoft, HubSpot Academy, DELE).',
            'Yes: a long course tied to a project already in Experience or Projects.',
            'No: 15 Udemy intros from the same year.',
            'No: repeating the same word seven times in Skills and Courses (stuffing).',
            'No: treating the platform as a university (“Udemy master’s”).',
          ],
        },
        {
          type: 'p',
          text: 'Before: Additional training — 18 courses (Udemy, Domestika, YouTube). After: Google UX Design (Coursera, 2024) — Figma prototype of a clinic booking app. Excel (Power Query) used in monthly reporting on my internship. Two lines. ATS finds Figma and Excel. A human has a smart question.',
        },
        { type: 'h2', text: 'Where to put it' },
        {
          type: 'p',
          text: 'If the course is the only signal of a tool the ad asks for, put it in Skills and in a bullet (“modelled the funnel in GA4 after course X”). If it is extra, a short “Certifications” block at the end, with year. Do not put it above real internships. A junior mid-degree should not open with Udemy. See [internship CV](/blog/cv-para-practicas).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'ATS looks for Excel, not the Udemy logo',
          lead: 'If your PDF lists 40 courses, the filter is not impressed. Keep four you can defend and regenerate the file.',
          from: '40 diplomas',
          to: '4 defensible skills',
          button: 'Tidy my training',
        },
        { type: 'h2', text: 'Coursera, LinkedIn Learning, Platzi, Domestika' },
        {
          type: 'p',
          text: 'The platform is not prestige by itself. Coursera with a partner university and a project > a one-video LinkedIn Learning. Platzi or a school known in your market can be a local signal. Domestika: useful in design if there is a portfolio; useless as a list. Do not lie about duration or “professional certificate” if it was a free course.',
        },
        { type: 'h2', text: 'Clean the PDF you already have' },
        {
          type: 'p',
          text: 'Delete the list, keep four, move tools into Skills as text (no bars). In ClarCV you upload Word or PDF, see density and structure, apply an ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. The model must not invent an AWS Certified that is not in the file.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I add the certificate URL?',
              a: 'If it is short and public, yes, as text. A huge QR, no. ATS does not follow the QR.',
            },
            {
              q: 'Does a 6-month bootcamp count as education?',
              a: 'Yes, under Education, with dates and a project. Do not hide it among Udemy micros.',
            },
            {
              q: 'What about company courses (Salesforce Trailhead)?',
              a: 'They count if the ad asks for that tool. One line with the level (Ranger, etc.) if it is recognisable.',
            },
            {
              q: 'Should I list courses abandoned at 40%?',
              a: 'No.',
            },
            {
              q: 'Does ClarCV pick my four courses?',
              a: 'You trim. Analysis may flag that the section is bloated.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'iconos-en-el-cv',
    date: '2026-08-22',
    category: 'format',
    related: ['cv-canva-y-ats', 'color-y-fuente-cv', 'errores-ats-que-descartan-tu-cv'],
    es: {
      title: 'Iconos y barras de skills en el CV: por qué el ATS no los lee',
      description:
        'El teléfono-dibujo y el Excel al 80% no son keywords. Prueba de dos minutos y cómo pasar a texto parseable. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Los iconos en el CV y las barras de habilidades se ven “de diseñador”. El ATS no diseña: extrae texto. Un teléfono que es un pictograma no entra en el índice. Un “Excel 80%” es un rectángulo de color, no la palabra Excel. Quien busca esto suele tener un Canva o una plantilla creativa que en pantalla se ve bien y al pegar en el bloc de notas sale vacío o desordenado. Es el mismo fallo que en [CV de Canva y ATS](/blog/cv-canva-y-ats) y en [errores ATS](/blog/errores-ats-que-descartan-tu-cv).',
        },
        {
          type: 'p',
          text: 'Esta guía explica qué iconos rompen el parseo, por qué las barras de skill mienten (¿80% comparado con quién?), y cómo sustituirlos por palabras sin ponerte “aburrido”. Las [plantillas ATS](/blog/plantillas-cv-compatibles-ats) ganan aquí no por estética, sino porque el email sigue siendo un email. ClarCV: subes el PDF, detectas fricción de estructura, regeneras una columna con contacto y skills en texto.',
        },
        {
          type: 'p',
          text: 'No vas a prohibirte todo lo visual para siempre. Vas a tener dos archivos si hace falta: uno para el portal, uno para la entrevista de diseño. El que falla es usar el infográfico en Workday.',
        },
        { type: 'h2', text: 'Qué no puede leer el parser' },
        {
          type: 'p',
          text: 'Iconos de email, teléfono, LinkedIn, pin de ubicación, globos de idioma, logos de Python en lugar de la palabra Python, QR, y texto convertido en curvas al exportar. También tablas usadas como layout y text boxes flotantes. Tú ves una cabecera elegante. El software lee el sidebar primero o se come el @ del correo.',
        },
        {
          type: 'ul',
          items: [
            'Sustituye el icono por la etiqueta: Email, Teléfono, LinkedIn + URL en texto.',
            'Idiomas: “Inglés B2”, no cinco puntos. Ver [idiomas en el CV](/blog/idiomas-en-el-cv).',
            'Skills: lista “Excel, Power Query, SAP MM”, no barras.',
            'Foto: opcional según país; si va, no debe ser la única capa con tu nombre.',
            'QR: el ATS no lo escanea. Un humano en feria sí; entonces es otro canal.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: una fila de iconos y “Office” con barra al 90%. Después: Email: ana@… | Tel. +34 … | LinkedIn: linkedin.com/in/ana — Habilidades: Excel (tablas dinámicas), Word, Outlook. El segundo bloque se busca. El primero se adivina.',
        },
        { type: 'h2', text: 'Por qué las barras de skills son peores que el silencio' },
        {
          type: 'p',
          text: '“Photoshop 70%” no significa nada en una oferta que pide “retoca 20 piezas/semana en Photoshop”. El porcentaje no es una keyword y además invita a discutir. Un junior pone 90% en todo; un senior honesto parece peor. Mejor: la herramienta + el contexto en una viñeta. Si no hay contexto, una lista plana. El reclutador asume que no eres nativo de todas.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Si el teléfono es un dibujo, el ATS no tiene teléfono',
          lead: 'Haz la prueba del bloc de notas. Si no se copia el email, regenera el PDF con iconos convertidos en palabras.',
          from: 'Iconos y barras',
          to: 'Contacto y skills en texto',
          button: 'Regenerar sin iconos',
        },
        { type: 'h2', text: 'La prueba de dos minutos' },
        {
          type: 'p',
          text: 'Selecciona cabecera y skills. Pega en el bloc de notas. ¿Salen email y “Excel”? Si no, no envíes ese archivo a un portal. Más detalle de diseño frágil: guía Canva. Más de encabezados raros (“Mis superpoderes”): errores ATS.',
        },
        { type: 'h2', text: 'Cómo salir de Canva sin redibujar a mano' },
        {
          type: 'p',
          text: 'Copia el contenido, no el lienzo. En ClarCV subes el PDF o Word, el análisis señala claridad y fricción (no un % mágico contra Workday), aplicas plantilla A4 parseable. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Conserva hechos; cambia el continente visual.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Ningún icono, nunca?',
              a: 'En el PDF del portal, evita iconos que sustituyan texto. Un punto o una raya simple no suele romper. El pictograma de teléfono sí.',
            },
            {
              q: '¿Y un CV creativo para un estudio?',
              a: 'Adjunta el parseable al portal y el visual por email o portfolio. No un solo infográfico en Greenhouse.',
            },
            {
              q: '¿Las barras de LinkedIn también sobran en el PDF?',
              a: 'LinkedIn es otra interfaz. El PDF no es LinkedIn. No copies las barras al archivo.',
            },
            {
              q: '¿El ATS lee imágenes alt?',
              a: 'En un PDF de CV, casi nunca. No cuentes con alt text de Canva.',
            },
            {
              q: '¿ClarCV quita los iconos solo?',
              a: 'Al aplicar una plantilla ATS, el contenido pasa a texto. Revisa que el teléfono y el email hayan sobrevivido.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Icons and skill bars on a CV: why ATS cannot read them',
      description:
        'A phone-as-drawing and Excel at 80% are not keywords. A two-minute test and how to switch to parseable text. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Icons on a CV and skill bars look “designed”. ATS does not design: it extracts text. A phone that is a pictogram never enters the index. “Excel 80%” is a coloured rectangle, not the word Excel. People searching this usually have a Canva or creative template that looks fine on screen and pastes empty or shuffled into Notepad. It is the same failure as [Canva CV and ATS](/blog/cv-canva-y-ats) and [ATS mistakes](/blog/errores-ats-que-descartan-tu-cv).',
        },
        {
          type: 'p',
          text: 'This guide explains which icons break parsing, why skill bars lie (80% compared with whom?), and how to replace them with words without looking “boring”. [ATS templates](/blog/plantillas-cv-compatibles-ats) win here not on taste, but because email is still the word email. ClarCV: upload the PDF, catch structure friction, regenerate one column with contact and skills as text.',
        },
        {
          type: 'p',
          text: 'You will not ban all visuals forever. You will keep two files if needed: one for the portal, one for a design interview. The failure is using the infographic in Workday.',
        },
        { type: 'h2', text: 'What the parser cannot read' },
        {
          type: 'p',
          text: 'Icons for email, phone, LinkedIn, location pin, language bubbles, Python logos instead of the word Python, QR codes, and text outlined on export. Also tables used as layout and floating text boxes. You see an elegant header. The software reads the sidebar first or eats the @ in the address.',
        },
        {
          type: 'ul',
          items: [
            'Replace the icon with a label: Email, Phone, LinkedIn + URL as text.',
            'Languages: “English B2”, not five dots. See [languages on a CV](/blog/idiomas-en-el-cv).',
            'Skills: list “Excel, Power Query, SAP MM”, not bars.',
            'Photo: optional by country; if it stays, it must not be the only layer with your name.',
            'QR: ATS does not scan it. A human at a fair might; that is another channel.',
          ],
        },
        {
          type: 'p',
          text: 'Before: a row of icons and “Office” with a bar at 90%. After: Email: ana@… | Tel. +34 … | LinkedIn: linkedin.com/in/ana — Skills: Excel (pivot tables), Word, Outlook. The second block is searchable. The first is guessed.',
        },
        { type: 'h2', text: 'Why skill bars are worse than silence' },
        {
          type: 'p',
          text: '“Photoshop 70%” means nothing in an ad that wants “retouch 20 assets/week in Photoshop”. The percentage is not a keyword and it invites an argument. Juniors put 90% on everything; an honest senior looks worse. Better: the tool + context in a bullet. If there is no context, a flat list. The recruiter assumes you are not native in all of them.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'If the phone is a drawing, ATS has no phone',
          lead: 'Do the Notepad test. If the email does not copy, regenerate the PDF with icons turned into words.',
          from: 'Icons and bars',
          to: 'Contact and skills as text',
          button: 'Regenerate without icons',
        },
        { type: 'h2', text: 'The two-minute test' },
        {
          type: 'p',
          text: 'Select the header and skills. Paste into Notepad. Do email and “Excel” appear? If not, do not send that file to a board. More on fragile design: Canva guide. More on odd headings (“My superpowers”): ATS mistakes.',
        },
        { type: 'h2', text: 'How to leave Canva without redrawing by hand' },
        {
          type: 'p',
          text: 'Copy the content, not the canvas. In ClarCV you upload PDF or Word, analysis flags clarity and friction (not a magic % against Workday), you apply a parseable A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Keep facts; change the visual container.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'No icons, ever?',
              a: 'On the portal PDF, avoid icons that replace text. A simple bullet or dash rarely breaks. A phone pictogram does.',
            },
            {
              q: 'What about a creative CV for a studio?',
              a: 'Attach the parseable file to the portal and the visual one by email or portfolio. Not a single infographic in Greenhouse.',
            },
            {
              q: 'Do LinkedIn skill bars belong on the PDF too?',
              a: 'LinkedIn is another UI. The PDF is not LinkedIn. Do not copy the bars onto the file.',
            },
            {
              q: 'Does ATS read image alt text?',
              a: 'On a CV PDF, almost never. Do not rely on Canva alt text.',
            },
            {
              q: 'Does ClarCV remove icons on its own?',
              a: 'When you apply an ATS template, content becomes text. Check that phone and email survived.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
